import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #shopMenuFlyoutPanelHomes;
  #shopMenuMobileHostHome;
  #shopMenuFlyoutController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupShopMenuFlyouts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#shopMenuFlyoutController?.abort();
    this.#closeShopMenuFlyouts();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.#closeShopMenuFlyouts();

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Restore the shop drawer's default featured card.
   */
  #closeShopMenuFlyouts() {
    const layout = this.refs.menuDrawer.querySelector('.shop-menu-drawer__layout');
    const triggers = this.refs.menuDrawer.querySelectorAll('[data-shop-menu-flyout-trigger]');
    const panels = this.refs.menuDrawer.querySelectorAll('[data-shop-menu-flyout-panel]');

    layout?.classList.remove('shop-menu-drawer__layout--flyout-active');

    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    panels.forEach((panel) => {
      this.#restoreShopMenuFlyoutPanel(panel);
      panel.classList.remove('is-active');
      panel.setAttribute('hidden', '');
    });

    const host = this.refs.menuDrawer.querySelector('[data-shop-menu-mobile-flyout-host]');
    const home = this.#shopMenuMobileHostHome;
    if (host && home?.parent && host.parentNode !== home.parent) {
      home.parent.insertBefore(host, home.nextSibling?.parentNode === home.parent ? home.nextSibling : null);
    }
  }

  /**
   * Connect custom shop drawer links to their matching flyout panels.
   */
  #setupShopMenuFlyouts() {
    const layout = this.refs.menuDrawer.querySelector('.shop-menu-drawer__layout');
    const triggers = this.refs.menuDrawer.querySelectorAll('[data-shop-menu-flyout-trigger]');
    const panels = this.refs.menuDrawer.querySelectorAll('[data-shop-menu-flyout-panel]');
    const mobileFlyoutHost = this.refs.menuDrawer.querySelector('[data-shop-menu-mobile-flyout-host]');

    if (!layout || !triggers.length || !panels.length) return;

    this.#shopMenuFlyoutController?.abort();
    this.#shopMenuFlyoutController = new AbortController();
    const listenerOptions = { signal: this.#shopMenuFlyoutController.signal };
    const mobileQuery = window.matchMedia('(max-width: 989px)');
    if (mobileFlyoutHost) {
      this.#shopMenuMobileHostHome = {
        parent: mobileFlyoutHost.parentNode,
        nextSibling: mobileFlyoutHost.nextSibling,
      };
    }

    let closeTimer;
    const panelHomes = new WeakMap();

    panels.forEach((panel) => {
      panelHomes.set(panel, {
        parent: panel.parentNode,
        nextSibling: panel.nextSibling,
      });
    });

    this.#shopMenuFlyoutPanelHomes = panelHomes;

    const isMobileFlyout = () => mobileQuery.matches;

    const clearCloseTimer = () => {
      if (!closeTimer) return;
      window.clearTimeout(closeTimer);
      closeTimer = undefined;
    };

    const closeFlyouts = () => {
      clearCloseTimer();
      this.#closeShopMenuFlyouts();
    };

    const openFlyout = (flyoutKey) => {
      clearCloseTimer();
      layout.classList.add('shop-menu-drawer__layout--flyout-active');
      const renderInMobileHost = isMobileFlyout() && mobileFlyoutHost;
      const activeTrigger = Array.from(triggers).find((trigger) => trigger.dataset.shopMenuFlyoutTrigger === flyoutKey);
      if (renderInMobileHost && activeTrigger) {
        activeTrigger.insertAdjacentElement('afterend', mobileFlyoutHost);
      }

      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.shopMenuFlyoutTrigger === flyoutKey;
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.shopMenuFlyoutPanel === flyoutKey;

        if (renderInMobileHost && isActive) {
          mobileFlyoutHost.append(panel);
        } else {
          this.#restoreShopMenuFlyoutPanel(panel);
        }

        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (!isMobileFlyout()) return;

        event.preventDefault();
        if (trigger.getAttribute('aria-expanded') === 'true') {
          closeFlyouts();
        } else {
          openFlyout(trigger.dataset.shopMenuFlyoutTrigger);
        }
      }, listenerOptions);

      trigger.addEventListener('mouseenter', () => {
        if (isMobileFlyout()) return;
        openFlyout(trigger.dataset.shopMenuFlyoutTrigger);
      }, listenerOptions);

      trigger.addEventListener('focus', () => {
        if (isMobileFlyout()) return;
        openFlyout(trigger.dataset.shopMenuFlyoutTrigger);
      }, listenerOptions);
    });

    panels.forEach((panel) => {
      panel.querySelector('[data-shop-menu-flyout-back]')?.addEventListener('click', () => {
        const trigger = Array.from(triggers).find((item) => item.dataset.shopMenuFlyoutTrigger === panel.dataset.shopMenuFlyoutPanel);
        closeFlyouts();
        trigger?.focus({ preventScroll: true });
      }, listenerOptions);
    });

    mobileQuery.addEventListener('change', closeFlyouts, listenerOptions);
    layout.addEventListener('mouseenter', clearCloseTimer, listenerOptions);

    layout.addEventListener('mouseleave', () => {
      if (isMobileFlyout()) return;
      closeTimer = window.setTimeout(closeFlyouts, 120);
    }, listenerOptions);

    this.refs.details.addEventListener('toggle', () => {
      if (!this.isOpen) closeFlyouts();
    }, listenerOptions);
  }

  /**
   * Restore a flyout panel to its desktop position after it has been moved into
   * the mobile drawer flow.
   * @param {Element} panel
   */
  #restoreShopMenuFlyoutPanel(panel) {
    const home = this.#shopMenuFlyoutPanelHomes?.get(panel);

    if (!home?.parent || panel.parentNode === home.parent) return;

    if (home.nextSibling?.parentNode === home.parent) {
      home.parent.insertBefore(panel, home.nextSibling);
      return;
    }

    home.parent.append(panel);
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

if (!customElements.get('shop-menu-banner')) {
  customElements.define('shop-menu-banner', class extends HTMLElement {
    connectedCallback() {
      this.fontController = new AbortController();
      this.observer = new ResizeObserver(() => this.fitTitle());
      this.observer.observe(this);
      document.fonts.addEventListener('loadingdone', () => this.fitTitle(), { signal: this.fontController.signal });
      document.fonts.ready.then(() => {
        if (this.isConnected) this.fitTitle();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.fontController?.abort();
    }

    fitTitle() {
      const title = this.querySelector('.shop-menu-drawer__location-title');
      if (!title || !this.clientWidth) return;
      const textOnly = this.parentElement.classList.contains('shop-menu-drawer__location-banner--text-only');
      const desiredSize = textOnly ? 18 : parseFloat(getComputedStyle(this).getPropertyValue('--location-banner-font-size')) || 72;
      title.style.fontSize = `${Math.min(desiredSize, this.clientHeight)}px`;
      const availableWidth = title.clientWidth;
      if (title.scrollWidth > availableWidth && availableWidth > 0) {
        const size = parseFloat(title.style.fontSize) * availableWidth / title.scrollWidth;
        title.style.fontSize = `${Math.floor(size)}px`;
      }
    }
  });
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
